import type { ReactNode } from "react";
import { Label } from "@/components/ui/Input";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import {
  formatZplPrinterLocationStockDetail,
  formatZplPrinterOptionLabel,
  ZPL_DEFAULT_PRINTER_NAME,
  type ZplPrintersQuerySnapshot,
} from "@/lib/zplPrinter";
import { cn } from "@/lib/utils";

export const ZPL_PRINTER_SELECT_HOVER_HINT =
  "Label stock loaded on this queue (width × height inches). Pick a queue whose stock matches the label you are printing.";

export function ZplPrinterReachabilityDot({
  online,
  className,
}: {
  online?: boolean | null;
  className?: string;
}): JSX.Element | null {
  if (online == null) return null;
  const title = online ? "Printer online" : "Printer offline";
  return (
    <span
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        online ? "bg-emerald-500" : "bg-rose-500",
        className,
      )}
      title={title}
      aria-hidden
    />
  );
}

function reachabilityLeading(online?: boolean | null): JSX.Element | undefined {
  if (online == null) return undefined;
  return <ZplPrinterReachabilityDot online={online} />;
}

export function ZplPrinterSelect({
  id,
  label = "Label printer",
  showLabel = true,
  size = "default",
  printersQuery,
  value,
  onChange,
  disabled = false,
  className,
  triggerClassName,
  panelMinWidthPx,
  listLayout = "wrap",
  triggerMode = "labeled",
  triggerIcon,
  ariaSummary,
  onListOpenChange,
  hoverHint = ZPL_PRINTER_SELECT_HOVER_HINT,
}: {
  id: string;
  label?: string;
  showLabel?: boolean;
  size?: "compact" | "default" | "touch";
  printersQuery: ZplPrintersQuerySnapshot;
  value: string;
  onChange: (printer: string) => void;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  panelMinWidthPx?: number;
  listLayout?: "wrap" | "single-line";
  triggerMode?: "labeled" | "icon";
  triggerIcon?: ReactNode;
  /** Used for icon trigger aria-label when showLabel is false. */
  ariaSummary?: string;
  onListOpenChange?: (open: boolean) => void;
  hoverHint?: string;
}): JSX.Element {
  const printers = printersQuery.data?.printers;
  const labelClassName =
    size === "touch"
      ? "text-[10px] font-semibold uppercase tracking-wide text-slate-500"
      : undefined;

  const options =
    printers != null && printers.length > 0
      ? printers.map((printerRow) => ({
          value: printerRow.name,
          label: formatZplPrinterOptionLabel(printerRow),
          detail: formatZplPrinterLocationStockDetail(printerRow, printers),
          leading: reachabilityLeading(printerRow.online),
        }))
      : [
          {
            value: value !== "" ? value : ZPL_DEFAULT_PRINTER_NAME,
            label: value !== "" ? value : ZPL_DEFAULT_PRINTER_NAME,
          },
        ];

  return (
    <div className={cn("flex flex-col items-stretch gap-0.5", className)}>
      {showLabel ? (
        <div className="flex items-center gap-1">
          <Label htmlFor={id} className={labelClassName}>
            {label}
          </Label>
          {hoverHint !== "" ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
        </div>
      ) : null}
      <HubTouchSelect
        id={id}
        aria-label={
          showLabel
            ? undefined
            : triggerMode === "icon" && (ariaSummary?.trim() ?? "") !== ""
              ? `Label printer: ${ariaSummary?.trim() ?? ""}`
              : label
        }
        size={size}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled || printersQuery.isLoading}
        triggerClassName={triggerClassName}
        panelMinWidthPx={panelMinWidthPx}
        triggerMode={triggerMode}
        triggerIcon={triggerIcon}
        onOpenChange={onListOpenChange}
        listItemClassName={
          listLayout === "single-line"
            ? "whitespace-nowrap text-sm py-2 min-h-10"
            : size === "compact"
              ? "text-xs leading-snug whitespace-normal py-2 min-h-0"
              : undefined
        }
      />
    </div>
  );
}

export function ZplPrinterListStatus({
  printersQuery,
  className,
}: {
  printersQuery: ZplPrintersQuerySnapshot;
  className?: string;
}): JSX.Element | null {
  if (printersQuery.isError) {
    return (
      <p className={cn("text-xs text-amber-700", className)}>
        {printersQuery.error instanceof Error
          ? printersQuery.error.message
          : "Could not load printers."}
      </p>
    );
  }
  if (printersQuery.data?.printerListSource === "default" && !printersQuery.isLoading) {
    return (
      <p className={cn("text-xs text-slate-500", className)}>
        Printer list is a single default until an administrator publishes the ZPL printer registry (Admin →
        ZPL printers).
      </p>
    );
  }
  return null;
}
